// ─────────────────────────────────────────────────────────────────────────────
// Shared flight utility functions and constants
// Used by SearchFlights.js, Home.js, and any future flight-result views.
// ─────────────────────────────────────────────────────────────────────────────

export const AIRLINE_NAMES = {
  A1: 'A.P.G. Distribution System',
  AA: 'American Airlines',
  AC: 'Air Canada',
  AF: 'Air France',
  AI: 'Air India',
  AM: 'Aeromexico',
  AS: 'Alaska Airlines',
  AY: 'Finnair',
  AZ: 'ITA Airways',
  BA: 'British Airways',
  BR: 'EVA Air',
  CA: 'Air China',
  CI: 'China Airlines',
  CX: 'Cathay Pacific',
  CZ: 'China Southern Airlines',
  DL: 'Delta Air Lines',
  EK: 'Emirates',
  ET: 'Ethiopian Airlines',
  EY: 'Etihad Airways',
  FJ: 'Fiji Airways',
  GA: 'Garuda Indonesia',
  GF: 'Gulf Air',
  HU: 'Hainan Airlines',
  IB: 'Iberia',
  JL: 'Japan Airlines',
  JQ: 'Jetstar Airways',
  KE: 'Korean Air',
  KL: 'KLM Royal Dutch Airlines',
  KU: 'Kuwait Airways',
  LA: 'LATAM Airlines',
  LH: 'Lufthansa',
  LO: 'LOT Polish Airlines',
  LX: 'SWISS',
  MH: 'Malaysia Airlines',
  MU: 'China Eastern Airlines',
  NH: 'All Nippon Airways',
  NZ: 'Air New Zealand',
  OA: 'Olympic Air',
  OD: 'Batik Air Malaysia',
  OS: 'Austrian Airlines',
  OZ: 'Asiana Airlines',
  PR: 'Philippine Airlines',
  QF: 'Qantas',
  QR: 'Qatar Airways',
  RO: 'Tarom',
  SA: 'South African Airways',
  SK: 'Scandinavian Airlines',
  SN: 'Brussels Airlines',
  SQ: 'Singapore Airlines',
  SU: 'Aeroflot',
  SV: 'Saudia',
  TG: 'Thai Airways',
  TK: 'Turkish Airlines',
  TP: 'TAP Air Portugal',
  UL: 'SriLankan Airlines',
  UX: 'Air Europa',
  VA: 'Virgin Australia',
  VJ: 'VietJet Air',
  VN: 'Vietnam Airlines',
  VS: 'Virgin Atlantic',
  WY: 'Oman Air',
  XQ: 'SunExpress',
  ZH: 'Shenzhen Airlines',
  '6E': 'IndiGo',
  '9W': 'Jet Airways',
  GP: 'APG Airlines',
  HR: 'Hahn Air',
};

/**
 * Airline On-Time Performance Scores (0-100)
 * Based on 2025 industry data: departure/arrival punctuality, cancellation rates, reliability
 * Higher score = more reliable airline (better candidate for Best Value)
 * 
 * Score ranges:
 * - 90-100: Excellent (top-tier airlines)
 * - 80-89: Very good (above industry average)
 * - 70-79: Good (around industry average)
 * - 60-69: Fair (below average)
 * - <60: Poor (frequent delays/cancellations)
 */
export const AIRLINE_ON_TIME_PERFORMANCE = {
  // Premium carriers (90-100)
  QF: 92,  // Qantas - Australian flagship, excellent reliability
  SQ: 90,  // Singapore Airlines - consistently top-rated
  NH: 91,  // All Nippon Airways - Japanese punctuality
  JL: 90,  // Japan Airlines - high operational standards
  LX: 89,  // SWISS - Swiss precision
  
  // Major full-service carriers (80-89)
  EK: 88,  // Emirates - large but reliable
  QR: 87,  // Qatar Airways - strong Middle East carrier
  CX: 86,  // Cathay Pacific - Hong Kong hub efficiency
  LH: 85,  // Lufthansa - German efficiency
  BA: 84,  // British Airways - improved operations
  AF: 82,  // Air France - solid European carrier
  KL: 83,  // KLM - Amsterdam hub reliability
  DL: 84,  // Delta Air Lines - US leader in on-time
  AA: 81,  // American Airlines - improved performance
  TG: 82,  // Thai Airways - reliable Asian carrier
  AY: 86,  // Finnair - efficient Nordic operations
  OS: 85,  // Austrian Airlines - Vienna hub
  SK: 84,  // Scandinavian Airlines - Nordic reliability
  TK: 83,  // Turkish Airlines - improving operations
  EY: 85,  // Etihad Airways - Abu Dhabi quality
  NZ: 88,  // Air New Zealand - top Southern Hemisphere
  VA: 85,  // Virgin Australia - strong domestic carrier
  KE: 86,  // Korean Air - Asian excellence
  OZ: 85,  // Asiana Airlines - reliable Korean carrier
  BR: 87,  // EVA Air - Taiwanese quality
  CI: 84,  // China Airlines - improving standards
  
  // Mid-tier carriers (70-79)
  AI: 76,  // Air India - improving after privatization
  AC: 79,  // Air Canada - variable performance
  AS: 80,  // Alaska Airlines - strong US regional
  IB: 78,  // Iberia - Spanish flagship
  AZ: 77,  // ITA Airways - new Italian carrier
  TP: 76,  // TAP Air Portugal - acceptable reliability
  SN: 77,  // Brussels Airlines - decent European carrier
  UX: 75,  // Air Europa - Spanish budget-friendly
  MH: 74,  // Malaysia Airlines - rebuilding reputation
  GA: 73,  // Garuda Indonesia - inconsistent
  PR: 75,  // Philippine Airlines - moderate reliability
  VN: 76,  // Vietnam Airlines - growing carrier
  ET: 78,  // Ethiopian Airlines - African leader
  SA: 72,  // South African Airways - operational challenges
  SV: 74,  // Saudia - improving Middle East carrier
  WY: 77,  // Oman Air - decent Gulf carrier
  GF: 75,  // Gulf Air - moderate reliability
  KU: 73,  // Kuwait Airways - acceptable performance
  UL: 74,  // SriLankan Airlines - variable service
  FJ: 76,  // Fiji Airways - Pacific reliability
  LA: 77,  // LATAM Airlines - South American leader
  AM: 76,  // Aeromexico - improving Mexican carrier
  
  // Chinese carriers (70-79) - large volume, variable performance
  CA: 75,  // Air China - Beijing hub, moderate reliability
  MU: 74,  // China Eastern - Shanghai operations
  CZ: 73,  // China Southern - Guangzhou hub
  HU: 72,  // Hainan Airlines - growing carrier
  ZH: 71,  // Shenzhen Airlines - regional player
  
  // Budget/Low-cost carriers (60-69) - lower fares, more delays
  '6E': 68,  // IndiGo - Indian budget leader, acceptable reliability
  JQ: 67,   // Jetstar Airways - Qantas budget subsidiary
  VJ: 65,   // VietJet Air - Vietnamese budget carrier
  XQ: 66,   // SunExpress - Turkish leisure carrier
  
  // Aggregators and special cases (not rated)
  A1: 70,   // A.P.G. Distribution System - aggregator, variable
  GP: 70,   // APG Airlines - aggregator
  HR: 70,   // Hahn Air - ticket consolidator
  
  // Defunct or inactive
  '9W': 50,  // Jet Airways - ceased operations
  RO: 68,    // Tarom - Romanian carrier, operational challenges
  LO: 79,    // LOT Polish Airlines - improved recently
  OA: 72,    // Olympic Air - Greek domestic focus
  OD: 70,    // Batik Air Malaysia - limited data
  SU: 71,    // Aeroflot - Russian carrier, geopolitical impact
};

export const AGGREGATOR_CARRIER_CODES = new Set(['A1', 'GP', 'HR']);
export const BLOCKED_AIRLINE_NAMES = new Set(['A.P.G. Distribution System', 'Duffel Airways', 'Duffel']);
export const BLOCKED_CARRIER_CODES = new Set(['W2']);

export const TIME_FILTER_OPTIONS = [
  { key: 'before6', label: 'Before 6 AM', icon: '☼' },
  { key: 'morning', label: '6 AM - 12 PM', icon: '☀' },
  { key: 'afternoon', label: '12 PM - 6 PM', icon: '☀' },
  { key: 'after6', label: 'After 6 PM', icon: '☾' },
];

export const STOP_FILTER_OPTIONS = [
  { key: 'nonstop', label: 'Non Stop', value: 0 },
  { key: 'onestop', label: '1 Stop', value: 1 },
];

export const parseTimeToMinutes = (timeValue) => {
  const [hh, mm] = String(timeValue || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return (hh * 60) + mm;
};

export const getHourFromTime = (timeValue) => {
  const [hh] = String(timeValue || '').split(':').map(Number);
  return Number.isFinite(hh) ? hh : null;
};

export const matchesTimeFilter = (timeValue, filterKey) => {
  if (filterKey === 'any') return true;
  const hour = getHourFromTime(timeValue);
  if (hour == null) return false;
  if (filterKey === 'before6') return hour < 6;
  if (filterKey === 'morning') return hour >= 6 && hour < 12;
  if (filterKey === 'afternoon') return hour >= 12 && hour < 18;
  if (filterKey === 'after6') return hour >= 18;
  return true;
};

export const getStopCountFromFlight = (flight) => {
  if (Array.isArray(flight?.segments) && flight.segments.length > 0) {
    return Math.max(flight.segments.length - 1, 0);
  }
  const typeText = String(flight?.type || '').toLowerCase();
  if (typeText.includes('direct') || typeText.includes('non-stop') || typeText.includes('nonstop')) return 0;
  const stopMatch = typeText.match(/(\d+)\s*-?\s*stop/);
  return stopMatch ? Number(stopMatch[1]) : 0;
};

export const isSingleStopSameCarrier = (flight) => {
  const stopCount = getStopCountFromFlight(flight);
  if (stopCount === 0) return true;
  if (stopCount !== 1) return false;
  if (!Array.isArray(flight?.segments) || flight.segments.length < 2) return false;
  const carrierSet = new Set(
    flight.segments
      .map((segment) => String(segment?.carrierCode || '').toUpperCase())
      .filter(Boolean),
  );
  return carrierSet.size === 1;
};

export const parseDurationToMinutes = (durationValue) => {
  const durationText = String(durationValue || '').trim().toLowerCase();
  if (!durationText) return Number.POSITIVE_INFINITY;
  const hoursMatch = durationText.match(/(\d+)\s*h/);
  const minsMatch = durationText.match(/(\d+)\s*m/);
  const hours = Number(hoursMatch?.[1] || 0);
  const mins = Number(minsMatch?.[1] || 0);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return Number.POSITIVE_INFINITY;
  return (hours * 60) + mins;
};

export const getCarrierCodeFromFlight = (flight) => {
  const codeFromFlightNumber = String(flight?.code || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .slice(0, 2);
  const segmentCarrierCode = flight?.segments?.[0]?.carrierCode;
  if (segmentCarrierCode) {
    const normalizedSegmentCarrier = String(segmentCarrierCode).toUpperCase();
    if (
      codeFromFlightNumber
      && AIRLINE_NAMES[codeFromFlightNumber]
      && AGGREGATOR_CARRIER_CODES.has(normalizedSegmentCarrier)
    ) {
      return codeFromFlightNumber;
    }
    return normalizedSegmentCarrier;
  }
  const code = String(flight?.code || '').trim().toUpperCase();
  if (!code) return '';
  const compactCode = code.replace(/[\s-]/g, '');
  const carrierMatch = compactCode.match(/^([A-Z0-9]{2})/);
  return carrierMatch ? carrierMatch[1] : '';
};

export const getFlightAirlineName = (flight) => {
  const airlineName = String(flight?.airline || '').trim();
  const normalizedAirlineCode = airlineName.toUpperCase();
  const codeCarrier = String(flight?.code || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .slice(0, 2);
  const carrierCode = getCarrierCodeFromFlight(flight);
  if (codeCarrier && AIRLINE_NAMES[codeCarrier] && !AGGREGATOR_CARRIER_CODES.has(codeCarrier)) {
    return AIRLINE_NAMES[codeCarrier];
  }
  if (carrierCode && AIRLINE_NAMES[carrierCode]) return AIRLINE_NAMES[carrierCode];
  if (normalizedAirlineCode.length <= 3 && AIRLINE_NAMES[normalizedAirlineCode]) {
    return AIRLINE_NAMES[normalizedAirlineCode];
  }
  if (airlineName && airlineName.length > 3) return airlineName;
  if (airlineName && airlineName.length <= 3) {
    const resolvedName = AIRLINE_NAMES[normalizedAirlineCode];
    if (resolvedName) return resolvedName;
    return airlineName.length === 2 ? `Airline ${airlineName}` : airlineName;
  }
  return carrierCode ? `Airline ${carrierCode}` : 'Airline';
};

export const isBlockedAirlineFlight = (flight) => BLOCKED_AIRLINE_NAMES.has(getFlightAirlineName(flight));

export const getAirlineInitials = (airlineName) => {
  const words = String(airlineName || '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'FL';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
};

export const getStopLabel = (stopCode) => {
  const airportCode = String(stopCode || '').trim().toUpperCase();
  if (!airportCode) return 'Unknown stop';
  const AIRPORT_LABELS = {
    SGN: 'Ho Chi Minh City, Vietnam',
    KUL: 'Kuala Lumpur, Malaysia',
    BKK: 'Bangkok, Thailand',
    CMB: 'Colombo, Sri Lanka',
    SIN: 'Singapore, Singapore',
    DOH: 'Doha, Qatar',
    DXB: 'Dubai, United Arab Emirates',
    AUH: 'Abu Dhabi, United Arab Emirates',
  };
  return AIRPORT_LABELS[airportCode] || airportCode;
};

/**
 * Derives a stable grouping key for a flight so that multiple fare offers
 * for the same physical flight (same carrier, same flight number, same departure)
 * are collapsed into a single card with a fare picker.
 * Handles both SearchFlights (departureTime) and Home/enriched (departure) field names.
 */
export const getFlightGroupKey = (flight) => {
  const segs = flight?.segments || [];
  const carrier = (segs[0]?.carrierCode || String(flight?.code || '').split('-')[0] || '').toUpperCase();
  const num = (segs[0]?.flightNumber || String(flight?.code || '').split('-')[1] || '').replace(/\s/g, '');
  const date = String(flight?.date || '');
  const dep = String(flight?.departure || flight?.departureTime || '');
  if (!carrier || carrier === 'FL' || !num) {
    return flight?.code || Math.random().toString(36);
  }
  return `${carrier}-${num}-${date}-${dep}`;
};

export const formatShortDate = (dateValue) =>
  new Date(dateValue).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });

export const getArrivalDateLabel = (baseDate, departureTime, arrivalTime) => {
  const departureMinutes = parseTimeToMinutes(departureTime);
  const arrivalMinutes = parseTimeToMinutes(arrivalTime);
  if (departureMinutes == null || arrivalMinutes == null) return formatShortDate(baseDate);
  const arrivalDate = new Date(`${baseDate}T00:00:00`);
  if (Number.isNaN(arrivalDate.getTime())) return formatShortDate(baseDate);
  if (arrivalMinutes < departureMinutes) arrivalDate.setDate(arrivalDate.getDate() + 1);
  return arrivalDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
};

/**
 * Gets baggage allowance weight in kg for a flight (used in Best Value scoring and preferences filtering).
 * Checks segments for checked bag weight, falls back to fare conditions.
 * 
 * @param {Object} flight - Flight object with segments or fareConditions
 * @returns {number} Baggage allowance in kg
 */
export const getBaggageAllowanceKg = (flight) => {
  // Check segments for baggage info
  if (Array.isArray(flight?.segments) && flight.segments.length > 0) {
    const firstSegment = flight.segments[0];
    if (firstSegment?.baggageAllowance?.checkedBag?.weightKg > 0) {
      return firstSegment.baggageAllowance.checkedBag.weightKg;
    }
  }
  
  // Check fare conditions
  const fc = flight?.fareConditions;
  if (fc?.checkedBagWeightKg > 0) return fc.checkedBagWeightKg;
  if (fc?.checkedBagCount > 0) return fc.checkedBagCount * 23; // Assume 23kg per bag
  
  // Default assumptions by cabin class
  const cabinClass = String(flight?.class || 'Economy').toLowerCase();
  if (cabinClass.includes('business')) return 30;
  if (cabinClass.includes('premium')) return 25;
  return 20; // Economy default
};

/**
 * Checks if a flight has flexible fare conditions (refundable or changeable).
 * Flexible tickets are more valuable as they offer peace of mind for travelers.
 * 
 * @param {Object} flight - Flight object with fareConditions or fare details
 * @returns {boolean} True if flight is refundable or changeable
 */
export const isFlightFlexible = (flight) => {
  if (!flight) return false;
  
  // Check fareConditions for refund/change policies
  const fc = flight.fareConditions;
  if (fc) {
    // Check if explicitly marked as refundable or changeable
    if (fc.refundable === true || fc.changeable === true) return true;
    
    // Check change/refund policies (not "not_allowed")
    if (fc.change_before_departure?.allowed === true) return true;
    if (fc.refund_before_departure?.allowed === true) return true;
  }
  
  // Check fare brand names for flexibility indicators
  const fareBrand = String(flight.fare_brand_name || flight.fareBrandName || '').toLowerCase();
  if (fareBrand.includes('flex') || 
      fareBrand.includes('refundable') || 
      fareBrand.includes('changeable') ||
      fareBrand.includes('premium') ||
      fareBrand.includes('business')) {
    return true;
  }
  
  // Check if segments have flexible conditions
  if (Array.isArray(flight.segments)) {
    return flight.segments.some(seg => {
      const segFc = seg.fareConditions;
      return segFc?.refundable === true || 
             segFc?.changeable === true ||
             segFc?.change_before_departure?.allowed === true;
    });
  }
  
  return false;
};

/**
 * Gets the on-time performance score for an airline (0-100).
 * Higher score = more reliable airline (better candidate for Best Value).
 * Uses carrier code from flight segments or flight code.
 * 
 * @param {Object} flight - Flight object with segments or code
 * @returns {number} On-time performance score (0-100), default 75 if airline not found
 */
export const getAirlinePerformanceScore = (flight) => {
  if (!flight) return 75; // Industry average default
  
  // Try to get carrier code from first segment
  let carrierCode = null;
  if (Array.isArray(flight.segments) && flight.segments.length > 0) {
    carrierCode = flight.segments[0]?.carrierCode;
  }
  
  // Fall back to extracting from flight code (e.g., "QF-123" -> "QF")
  if (!carrierCode && flight.code) {
    const codeParts = String(flight.code).split('-');
    if (codeParts.length > 0) {
      carrierCode = codeParts[0].toUpperCase();
    }
  }
  
  // Fall back to airline field if available
  if (!carrierCode && flight.airline) {
    // Check if airline matches any AIRLINE_NAMES value, get the code
    const airlineLower = String(flight.airline).toLowerCase();
    for (const [code, name] of Object.entries(AIRLINE_NAMES)) {
      if (name.toLowerCase() === airlineLower) {
        carrierCode = code;
        break;
      }
    }
  }
  
  // Look up performance score
  if (carrierCode && AIRLINE_ON_TIME_PERFORMANCE[carrierCode] !== undefined) {
    return AIRLINE_ON_TIME_PERFORMANCE[carrierCode];
  }
  
  // Default to industry average if airline not found
  return 75;
};

/**
 * Calculates departure time bonus for LONG-HAUL flights (>= 8 hours).
 * Evening/late departures (7 PM - 1 AM) are preferred for overnight flights.
 * Peak preference at 10 PM - midnight (sleep on plane).
 * 
 * @param {Object} flight - Flight object with departureTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getLongHaulDepartureScore = (flight) => {
  if (!flight || !flight.departureTime) return 0;
  
  const hour = getHourFromTime(flight.departureTime);
  if (hour === null) return 0;
  
  // Preferred range: 19:00 (7 PM) to 01:00 (1 AM next day)
  // Peak: 22:00-00:00 (10 PM - midnight) = 15 points
  
  if (hour >= 19 && hour <= 23) {
    if (hour >= 22) return 15; // Peak hours: 10 PM - 11:59 PM
    return ((hour - 19) / 3) * 15; // Gradual increase: 7 PM = 0, scaling to 10 PM = 15
  } else if (hour >= 0 && hour <= 1) {
    return 15 - (hour * 2); // Slight decline: midnight = 15, 1 AM = 13
  }
  
  return 0; // Outside preferred window
};

/**
 * Calculates departure time bonus for SHORT-HAUL direct flights (< 4 hours).
 * Morning departures (6 AM - 12 PM) are preferred for day trips.
 * Peak preference at 7-9 AM (reasonable for most travelers).
 * 
 * @param {Object} flight - Flight object with departureTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getShortHaulDepartureScore = (flight) => {
  if (!flight || !flight.departureTime) return 0;
  
  const hour = getHourFromTime(flight.departureTime);
  if (hour === null) return 0;
  
  // Preferred range: 06:00 (6 AM) to 12:00 (12 PM)
  // Peak: 07:00-09:00 (7-9 AM) = 15 points
  
  if (hour >= 6 && hour <= 12) {
    if (hour >= 7 && hour <= 9) return 15; // Peak morning hours
    if (hour === 6) return 12; // Early but acceptable
    if (hour === 10) return 12; // Still good
    if (hour === 11) return 8; // Acceptable
    if (hour === 12) return 5; // Edge of window
  }
  
  return 0; // Outside preferred window
};

/**
 * Calculates departure time bonus for MEDIUM-HAUL direct flights (4-8 hours).
 * Morning departures (6 AM - 12 PM) are preferred for day flights.
 * Peak preference at 7-9 AM.
 * 
 * @param {Object} flight - Flight object with departureTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getMediumHaulDepartureScore = (flight) => {
  if (!flight || !flight.departureTime) return 0;
  
  const hour = getHourFromTime(flight.departureTime);
  if (hour === null) return 0;
  
  // Preferred range: 06:00 (6 AM) to 12:00 (12 PM)
  // Peak: 07:00-09:00 (7-9 AM) = 15 points
  
  if (hour >= 6 && hour <= 12) {
    if (hour >= 7 && hour <= 9) return 15; // Peak morning hours
    if (hour === 6) return 12; // Early but good
    if (hour === 10) return 10; // Still acceptable
    if (hour === 11) return 7; // Getting late
    if (hour === 12) return 4; // Edge of window
  }
  
  return 0; // Outside preferred window
};

/**
 * Calculates arrival time bonus for LONG-HAUL flights (>= 8 hours).
 * Early morning arrivals (6 AM - 12 PM) are preferred to start the day fresh.
 * Peak preference at 7-9 AM.
 * 
 * @param {Object} flight - Flight object with arrivalTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getLongHaulArrivalScore = (flight) => {
  if (!flight || !flight.arrivalTime) return 0;
  
  const hour = getHourFromTime(flight.arrivalTime);
  if (hour === null) return 0;
  
  // Preferred range: 06:00 (6 AM) to 12:00 (12 PM)
  // Peak: 07:00-09:00 (7-9 AM) = 15 points
  
  if (hour >= 6 && hour <= 12) {
    if (hour >= 7 && hour <= 9) return 15; // Peak hours
    if (hour === 6) return 12; // Early but good
    if (hour === 10) return 12; // Still good
    if (hour === 11) return 8; // Acceptable
    if (hour === 12) return 5; // Edge of window
  }
  
  return 0; // Outside preferred window
};

/**
 * Calculates arrival time bonus for MEDIUM-HAUL direct flights (4-8 hours).
 * Afternoon arrivals (1 PM - 6 PM) are preferred.
 * Peak preference at 1-3 PM (good balance for onward travel).
 * 
 * @param {Object} flight - Flight object with arrivalTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getMediumHaulArrivalScore = (flight) => {
  if (!flight || !flight.arrivalTime) return 0;
  
  const hour = getHourFromTime(flight.arrivalTime);
  if (hour === null) return 0;
  
  // Preferred range: 13:00 (1 PM) to 18:00 (6 PM)
  // Peak: 13:00-15:00 (1-3 PM) = 15 points
  
  if (hour >= 13 && hour <= 18) {
    if (hour >= 13 && hour <= 15) return 15; // Peak hours
    if (hour === 16) return 12; // Still good
    if (hour === 17) return 9; // Acceptable
    if (hour === 18) return 6; // Edge of window
  }
  
  return 0; // Outside preferred window
};

/**
 * Calculates arrival time bonus for SHORT-HAUL direct flights (< 4 hours).
 * Flexible timing - afternoon (for day flights) or evening (for after-work flights).
 * Broad window accommodates different traveler preferences.
 * 
 * @param {Object} flight - Flight object with arrivalTime
 * @returns {number} Bonus score (0-15 points), higher = better for Best Value
 */
const getShortHaulArrivalScore = (flight) => {
  if (!flight || !flight.arrivalTime) return 0;
  
  const hour = getHourFromTime(flight.arrivalTime);
  if (hour === null) return 0;
  
  // Preferred: Afternoon 1-5 PM (day flights) or Evening 7-10 PM (after-work flights)
  // Broad window acknowledges different use cases
  
  if (hour >= 13 && hour <= 17) {
    return 15; // 1 PM - 5 PM: ideal for day flights
  } else if (hour >= 19 && hour <= 22) {
    return 15; // 7 PM - 10 PM: ideal for evening/after-work flights
  }
  
  return 0; // Outside preferred windows
};

/**
 * Assigns smart tags ('Cheapest', 'Fastest', 'Best Value') to flights.
 * 
 * IMPROVED Logic (Hybrid Approach with Automatic Triple-Tagging):
 * - Cheapest: Lowest price (tiebreaker: shortest duration)
 * - Fastest: Shortest duration
 * - A flight CAN have both Cheapest and Fastest tags
 * - Best Value: Uses enhanced scoring with context-aware algorithm:
 *   score = price + (stops × 180) + (duration/2) - (baggage × weight) - (flexible ? bonus : 0) 
 *           - (airlineScore / 2) - departureScore - arrivalScore
 * - Triple-tagging: If a flight is BOTH Cheapest AND Fastest, it automatically gets Best Value too
 *   (logically, the cheapest and fastest option is the best value)
 * 
 * Bonus factors (all reduce score = improve ranking):
 * - Baggage: Context-aware weight
 *   - Long-haul: More baggage = -2 points per kg (30kg = -60 points)
 *   - Short/Medium-haul (direct < 8h): Less important = -1 point per kg (30kg = -30 points)
 * - Flexibility: Context-aware bonus
 *   - Long-haul: Refundable/changeable = -50 points (harder to rebook)
 *   - Short/Medium-haul (direct < 8h): Less important = -25 points (easier to rebook)
 * - Airline reliability: Higher on-time % = better (90% = -45 points)
 * - Departure time: Context-aware timing preferences (reduced weight for balance)
 *   - Short-haul (direct < 4h): Morning departures 6 AM-12 PM = -15 points max (7-9 AM peak)
 *   - Medium-haul (direct 4-8h): Morning departures 6 AM-12 PM = -15 points max (7-9 AM peak)
 *   - Long-haul (>= 8h): Evening departures 7 PM-1 AM = -15 points max (10 PM-midnight peak)
 * - Arrival time: Context-aware timing preferences (reduced weight for balance)
 *   - Short-haul (direct < 4h): Flexible arrivals 1-5 PM or 7-10 PM = -15 points max
 *   - Medium-haul (direct 4-8h): Afternoon arrivals 1 PM-6 PM = -15 points max (1-3 PM peak)
 *   - Long-haul (>= 8h): Morning arrivals 6 AM-12 PM = -15 points max (7-9 AM peak)
 * 
 * @param {Array} flights - Array of flight objects with price, duration, and code properties
 * @param {Function} getPriceFunc - Function to extract numeric price from a flight
 * @returns {Map<string, string[]>} Map of flight codes to arrays of their badge labels
 */
/**
 * Calculates total layover time for connecting flights.
 * Returns 0 for direct flights.
 * 
 * @param {Object} flight - Flight object with segments
 * @returns {number} Total layover duration in minutes
 */
const calculateLayoverDuration = (flight) => {
  if (!Array.isArray(flight?.segments) || flight.segments.length < 2) return 0;
  
  let totalLayover = 0;
  for (let i = 0; i < flight.segments.length - 1; i++) {
    const currentSegment = flight.segments[i];
    const nextSegment = flight.segments[i + 1];
    
    const arrivalTime = parseTimeToMinutes(currentSegment.arrivalTime);
    const nextDepartureTime = parseTimeToMinutes(nextSegment.departureTime);
    
    if (arrivalTime !== null && nextDepartureTime !== null) {
      let layover = nextDepartureTime - arrivalTime;
      // Handle overnight layovers (next day)
      if (layover < 0) layover += 1440; // Add 24 hours
      totalLayover += layover;
    }
  }
  
  return totalLayover;
};

export const assignFlightTags = (flights, getPriceFunc = (f) => Number(f.price || 0)) => {
  const badges = new Map();
  
  if (!flights || flights.length === 0) return badges;

  // STEP 1: Calculate Best Value scores for ALL flights first (needed for tiebreaker)
  const flightsWithScores = flights.map(f => {
    const price = getPriceFunc(f);
    const stops = getStopCountFromFlight(f);
    const stopPenalty = stops * 180;
    const duration = parseDurationToMinutes(f.duration);
    const baggageKg = getBaggageAllowanceKg(f);
    const flexible = isFlightFlexible(f);
    const airlineScore = getAirlinePerformanceScore(f);
    const isDirect = getStopCountFromFlight(f) === 0;
    
    // SHORT-HAUL vs MEDIUM-HAUL vs LONG-HAUL timing preferences
    // For direct flights < 4 hours: morning departures (7-9 AM peak) + flexible arrivals (afternoon or evening)
    // For direct flights 4-8 hours: morning departures (7-9 AM peak) + afternoon arrivals (1-3 PM peak)
    // For longer flights (>= 8 hours): evening departures (10 PM-midnight peak) + morning arrivals (7-9 AM peak)
    const isShortHaul = isDirect && duration < 240; // 240 minutes = 4 hours
    const isMediumHaul = isDirect && duration >= 240 && duration < 480; // 4-8 hours
    
    let departureScore, arrivalScore;
    if (isShortHaul) {
      departureScore = getShortHaulDepartureScore(f);
      arrivalScore = getShortHaulArrivalScore(f);
    } else if (isMediumHaul) {
      departureScore = getMediumHaulDepartureScore(f);
      arrivalScore = getMediumHaulArrivalScore(f);
    } else {
      departureScore = getLongHaulDepartureScore(f);
      arrivalScore = getLongHaulArrivalScore(f);
    }
    
    // Context-aware bonus weights for short/medium-haul vs long-haul
    // Short/Medium-haul (< 8 hours): Baggage and flexibility less important (people travel light, easier to rebook)
    // Long-haul (>= 8 hours): Baggage and flexibility more important (checked bags needed, harder to change plans)
    const isShortOrMedium = isDirect && duration < 480; // 480 minutes = 8 hours
    const baggageWeight = isShortOrMedium ? 1 : 2;  // Reduced by 50% for short/medium-haul
    const flexibilityBonus = isShortOrMedium ? 25 : 50;  // Reduced by 50% for short/medium-haul
    
    // NEW PENALTY 1: Layover duration penalty
    // Add actual layover time to scoring - long layovers are worse value
    const layoverDuration = calculateLayoverDuration(f);
    const layoverPenalty = layoverDuration; // 1 minute layover = 1 point penalty
    
    // NEW BONUS 2: Same-airline connection bonus
    // Single-airline connections are more reliable (baggage transfer, coordinated delays)
    const sameAirlineBonus = (stops === 1 && isSingleStopSameCarrier(f)) ? 30 : 0;
    
    // NEW PENALTY 3: Red-eye arrival penalty (1 AM - 4 AM)
    // Arrivals in the middle of the night are exhausting and inconvenient
    const arrivalHour = getHourFromTime(f.arrivalTime);
    const redEyePenalty = (arrivalHour >= 1 && arrivalHour < 4) ? 50 : 0;
    
    // NEW BONUS 4: Direct flight bonus (ONLY for flights under 10 hours)
    // Short/medium direct flights deserve preference over connections
    // Long-haul directs (>= 10h) compete normally without special bonus
    const directFlightBonus = (isDirect && duration < 600) ? 80 : 0; // 600 min = 10 hours
    
    // ENHANCED SCORING: All factors considered for Best Value
    // - Duration weight doubled (/2 instead of /4)
    // - Baggage bonus (context-aware: less important for short flights)
    // - Flexibility bonus (context-aware: less important for short flights)
    // - Airline reliability bonus (higher on-time % = -airlineScore/2 points)
    // - Departure time bonus (context-aware: short-haul prefers early, long-haul prefers evening)
    // - Arrival time bonus (context-aware: short-haul prefers evening, long-haul prefers early)
    // - Layover penalty: Actual connection time added to score (longer = worse)
    // - Same-airline bonus: -30 points for single-carrier 1-stop connections
    // - Red-eye penalty: +50 points for arrivals between 1-4 AM
    // - Direct flight bonus: -80 points for direct flights under 10 hours (encourages short/medium directs)
    // 
    // TODO: Add UI tooltip showing "Why Best Value?" breakdown for transparency:
    // - Price comparison (e.g., "15% cheaper than fastest option")
    // - Key advantages (e.g., "Qantas 92% on-time reliability")
    // - Timing benefits (e.g., "Optimal 7 PM departure for overnight travel")
    // - Baggage/flexibility benefits (e.g., "Includes 30kg checked baggage")
    const score = price + stopPenalty + (duration / 2) 
                  + layoverPenalty
                  + redEyePenalty
                  - (baggageKg * baggageWeight) 
                  - (flexible ? flexibilityBonus : 0) 
                  - (airlineScore / 2)
                  - departureScore
                  - arrivalScore
                  - sameAirlineBonus
                  - directFlightBonus;
    
    return { flight: f, score, price, duration, baggageKg, flexible, airlineScore, departureScore, arrivalScore, isDirect };
  });

  // STEP 2: Find cheapest flight
  const sortedByPrice = [...flights].sort((a, b) => {
    const priceA = getPriceFunc(a);
    const priceB = getPriceFunc(b);
    if (priceA !== priceB) return priceA - priceB;
    // Tiebreaker: prefer shorter duration
    return parseDurationToMinutes(a.duration) - parseDurationToMinutes(b.duration);
  });
  
  const cheapest = sortedByPrice[0];
  badges.set(cheapest.code, ['Cheapest']);

  // STEP 3: Find fastest flight (with Best Value score as tiebreaker)
  const sortedByDuration = [...flightsWithScores].sort((a, b) => {
    const durationA = a.duration;
    const durationB = b.duration;
    if (durationA !== durationB) return durationA - durationB;
    // Tiebreaker: prefer better Best Value score (lower = better)
    return a.score - b.score;
  });
  
  const fastest = sortedByDuration[0].flight;
  
  if (fastest.code === cheapest.code) {
    // Same flight is cheapest AND fastest
    const existing = badges.get(fastest.code) || [];
    badges.set(fastest.code, [...existing, 'Fastest']);
  } else {
    badges.set(fastest.code, ['Fastest']);
  }

  // STEP 4: Assign Best Value tags
  // Dynamic duration cap based on route characteristics:
  // - If direct flights exist: cap at 2x shortest direct flight duration (max 26 hours)
  // - If no direct flights: cap at 26 hours absolute maximum
  // This prevents ultra-long flights from getting Best Value even if cheap
  
  const directFlights = flightsWithScores.filter(f => f.isDirect);
  let maxBestValueDuration = 1560; // 26 hours absolute maximum
  
  if (directFlights.length > 0) {
    const shortestDirectDuration = Math.min(...directFlights.map(f => f.duration));
    // Cap at 2x the shortest direct flight, but never more than 26 hours
    maxBestValueDuration = Math.min(shortestDirectDuration * 2, 1560);
  }
  
  const reasonableFlights = flightsWithScores.filter(f => f.duration <= maxBestValueDuration);
  
  // Assign Best Value tags to top 3 flights by score (regardless of direct/connecting)
  // This gives users multiple good options based on the scoring algorithm
  if (reasonableFlights.length > 0) {
    // Sort all reasonable flights by score (lowest = best)
    reasonableFlights.sort((a, b) => a.score - b.score);
    
    // Assign up to 3 Best Value tags with star ratings
    if (reasonableFlights.length >= 1) {
      const existing = badges.get(reasonableFlights[0].flight.code) || [];
      badges.set(reasonableFlights[0].flight.code, [...existing, 'BEST VALUE ★★★']);
    }
    if (reasonableFlights.length >= 2) {
      const existing = badges.get(reasonableFlights[1].flight.code) || [];
      badges.set(reasonableFlights[1].flight.code, [...existing, 'BEST VALUE ★★']);
    }
    if (reasonableFlights.length >= 3) {
      const existing = badges.get(reasonableFlights[2].flight.code) || [];
      badges.set(reasonableFlights[2].flight.code, [...existing, 'BEST VALUE ★']);
    }
  }
  // Note: If ALL flights are over the duration cap, no Best Value tag is assigned (only Cheapest and Fastest)

  return badges;
};
